import React, { useState, useEffect } from 'react';
import {
  Box,
  Flex,
  Text,
  VStack,
  HStack,
  Avatar,
  Badge,
  Button,
  Divider,
  Spinner,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  Select,
  Textarea,
  Center,
  Icon,
  useBreakpointValue,
} from '@chakra-ui/react';
import { HiOutlineArrowNarrowLeft } from 'react-icons/hi';
import { FiAlertCircle } from 'react-icons/fi';
import { useData } from '../../../data';
import { CustomToast } from '../../../components/toast/CustomToast';
import moment from 'moment';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { mutateFunction, useMutation } from '../../../libs/apis';
import Header from '../../../components/header/Header';

const ProfileReportsDetailPage = () => {
  const [searchParams] = useSearchParams();
  const { mutate, isLoading: isActionLoading } = useMutation(mutateFunction);
  const reportId = searchParams.get('id');
  const navigate = useNavigate();
  const { addToast } = CustomToast();
  const [actionNote, setActionNote] = useState('');
  const [selectedAction, setSelectedAction] = useState('');
  const { isOpen, onOpen, onClose } = useDisclosure();

  const {
    data: reportData,
    isLoading,
    error,
    refetch,
  } = useData(`admins/user-reports/${reportId}`);

  const avatarSize = useBreakpointValue({
    base: 'md',
    sm: 'lg',
  });

  const handleAction = () => {
    if (!selectedAction) {
      addToast('Please select an action', 'error');
      return;
    }

    if (selectedAction === 'take_action' && !actionNote) {
      addToast('Please provide a note for the action', 'error');
      return;
    }

    const formattedValue = selectedAction
      .split('_')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');

    const payload = {
      reportId,
      action: formattedValue,
      ...(actionNote && { note: actionNote }),
    };

    mutate(
      {
        key: `admins/user-reports/${reportId}`,
        method: 'put',
        data: {
          status: payload?.action,
        },
      },
      {
        onSuccess: () => {
          addToast('Report status updated successfully', 'success');
          refetch();
          onClose();
        },
        onError: (error) => {
          addToast(
            error?.response?.data?.message || 'Failed to update report status',
            'error',
          );
        },
      },
    );
  };

  const goBack = () => {
    navigate(-1);
  };

  if (isLoading) {
    return (
      <Flex justify="center" align="center" h="100vh">
        <Spinner size="xl" />
      </Flex>
    );
  }

  if (error) {
    return (
      <Box p={4}>
        <Flex cursor="pointer" onClick={goBack} alignItems="center" mb={4}>
          <HiOutlineArrowNarrowLeft size="24px" />
          <Text ml={2} fontWeight="600">
            Back to Reports
          </Text>
        </Flex>

        <Center h="50vh" flexDirection="column" gap={4}>
          <Icon as={FiAlertCircle} w={12} h={12} color="red.500" />
          <Text fontSize="xl" fontWeight="bold" color="gray.700">
            Error Loading Report
          </Text>
          <Text color="gray.500" textAlign="center">
            {error?.response?.data?.message ||
              'Failed to load report details. Please try again.'}
          </Text>
          <Button
            colorScheme="blue"
            onClick={() => refetch()}
            leftIcon={<Icon as={FiAlertCircle} />}
          >
            Retry
          </Button>
        </Center>
      </Box>
    );
  }

  if (!reportData?.data) {
    return (
      <Box p={4}>
        <Flex cursor="pointer" onClick={goBack} alignItems="center" mb={4}>
          <HiOutlineArrowNarrowLeft size="24px" />
          <Text ml={2} fontWeight="600">
            Back to Reports
          </Text>
        </Flex>

        <Center h="50vh" flexDirection="column" gap={4}>
          <Icon as={FiAlertCircle} w={12} h={12} color="yellow.500" />
          <Text fontSize="xl" fontWeight="bold" color="gray.700">
            Report Not Found
          </Text>
          <Text color="gray.500" textAlign="center">
            The report you're looking for doesn't exist or has been removed.
          </Text>
          <Button colorScheme="blue" onClick={goBack}>
            Return to Reports
          </Button>
        </Center>
      </Box>
    );
  }

  const report = reportData?.data;
  const reportedUser = report?.user;
  const reporter = report?.reporter;

  return (
    <Box>
      <Header title="Report Details" />
      <Box p={4}>
        <Flex cursor="pointer" onClick={goBack} alignItems="center" mb={4}>
          <HiOutlineArrowNarrowLeft size="24px" />
          <Text ml={2} fontWeight="600">
            Back to Reports
          </Text>
        </Flex>

        <VStack spacing={6} align="stretch">
          {/* Report Status and Actions */}
          <Box bg="white" p={4} borderRadius="md" boxShadow="sm">
            <Flex justify="space-between" align="center" mb={4}>
              <Badge
                colorScheme={
                  {
                    New: 'blue',
                    'Under Review': 'yellow',
                    Resolved: 'green',
                    Dismissed: 'gray',
                  }[report?.status] || 'gray'
                }
                fontSize="md"
                px={3}
                py={1}
              >
                {report?.status}
              </Badge>
              <Button colorScheme="blue" onClick={onOpen}>
                Take Action
              </Button>
            </Flex>
            <Text color="gray.600" fontSize="sm">
              Reported on{' '}
              {moment(report?.createdAt).format('MMMM DD, YYYY HH:mm')}
            </Text>
          </Box>

          {/* Reported User Profile */}
          <Box bg="white" p={4} borderRadius="md" boxShadow="sm">
            <Text fontSize="lg" fontWeight="bold" mb={4}>
              Reported User Profile
            </Text>
            <HStack spacing={4} align="start">
              <Avatar
                size={avatarSize}
                name={`${reportedUser?.first_name} ${reportedUser?.last_name}`}
                src={reportedUser?.profile_image}
              />
              <VStack align="start" spacing={2}>
                <Text fontSize="xl" fontWeight="bold">
                  {`${reportedUser?.first_name} ${reportedUser?.last_name}`}
                </Text>
                <Text color="gray.600">{reportedUser?.email}</Text>
                <Text color="gray.600">
                  Joined{' '}
                  {moment(reportedUser?.createdAt).format('MMMM DD, YYYY')}
                </Text>
                <Text color="gray.600">
                  User Type:{' '}
                  {reportedUser?.user_type_id === 1
                    ? 'Founder'
                    : reportedUser?.user_type_id === 2
                      ? 'Investor'
                      : reportedUser?.user_type_id === 3
                        ? 'Expert'
                        : 'User'}
                </Text>
              </VStack>
            </HStack>
          </Box>

          {/* Report Details */}
          <Box bg="white" p={4} borderRadius="md" boxShadow="sm">
            <Text fontSize="lg" fontWeight="bold" mb={4}>
              Report Details
            </Text>
            <VStack align="start" spacing={4}>
              <Box>
                <Text fontWeight="bold" mb={2}>
                  Reported By
                </Text>
                <HStack spacing={3}>
                  <Avatar
                    size="sm"
                    name={`${reporter?.first_name} ${reporter?.last_name}`}
                    src={reporter?.profile_image}
                  />
                  <Text>{`${reporter?.first_name} ${reporter?.last_name}`}</Text>
                </HStack>
              </Box>
              <Box>
                <Text fontWeight="bold" mb={2}>
                  Reason
                </Text>
                <Text>{report?.reason}</Text>
              </Box>
              {report?.details && (
                <Box>
                  <Text fontWeight="bold" mb={2}>
                    Additional Details
                  </Text>
                  <Text>{report?.details}</Text>
                </Box>
              )}
            </VStack>
          </Box>
        </VStack>

        {/* Action Modal */}
        <Modal isOpen={isOpen} onClose={onClose}>
          <ModalOverlay />
          <ModalContent>
            <ModalHeader>Take Action</ModalHeader>
            <ModalCloseButton />
            <ModalBody>
              <VStack spacing={4}>
                <Select
                  placeholder="Select action"
                  value={selectedAction}
                  onChange={(e) => {
                    const value = e.target.value;
                    setSelectedAction(value);
                  }}
                >
                  <option value="under_review">Mark as Under Review</option>
                  <option value="resolved">Resolved</option>
                  <option value="dismissed">Dismiss Report</option>
                </Select>
                {selectedAction === 'take_action' && (
                  <Textarea
                    placeholder="Enter action details..."
                    value={actionNote}
                    onChange={(e) => setActionNote(e.target.value)}
                  />
                )}
              </VStack>
            </ModalBody>
            <ModalFooter>
              <Button variant="ghost" mr={3} onClick={onClose}>
                Cancel
              </Button>
              <Button
                colorScheme="blue"
                onClick={handleAction}
                isLoading={isActionLoading}
              >
                Submit
              </Button>
            </ModalFooter>
          </ModalContent>
        </Modal>
      </Box>
    </Box>
  );
};

export default ProfileReportsDetailPage;
